/**
 * @fileoverview Single order group with header and nested line items.
 * @module features/daily-summary/components/OrderGroup
 */

import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { codeToFlag } from '@/shared/utils/countryUtils'
import { useReportingCurrency } from '@/features/settings/context/OrgSettingsProvider'
import { formatMoney } from '@/shared/utils/money'
import { useNewOrderHighlights } from '@/features/daily-summary/hooks/newOrderHighlights'
import type { OrderGroup as OrderGroupType, OrderLineItem } from '@/features/daily-summary/hooks/useTodaysOrders'

interface OrderGroupProps {
  order: OrderGroupType
}

const FALLBACK_IMG = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0OCIgaGVpZ2h0PSI0OCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM1YTVhNmEiIHN0cm9rZS13aWR0aD0iMS41Ij48cmVjdCB4PSIzIiB5PSIzIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHJ4PSIyIi8+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiLz48cGF0aCBkPSJNMjEgMTVsLTUtNUw1IDIxIi8+PC9zdmc+'

export function OrderGroupCard({ order }: OrderGroupProps): ReactElement {
  const { t } = useTranslation('features-home')
  const navigate = useNavigate()
  const { currency } = useReportingCurrency()
  const newOrderIds = useNewOrderHighlights()
  const isNew = newOrderIds.has(order.orderId)
  const cur = currency ?? 'PLN'
  const fmt = (v: number): string => formatMoney(v, cur, undefined, 0, 0)

  const handleLineClick = (line: OrderLineItem): void => {
    if (!line.skuKey) return
    navigate(`/dashboard/product/${encodeURIComponent(line.skuKey)}`, {
      state: {
        productName: line.name,
        imageUrl: line.imageUrl,
      },
    })
  }

  return (
    <div className="border-b border-border/50 last:border-b-0 transition-colors hover:bg-foreground/[0.01]">
      {/* Order header */}
      <div className="grid grid-cols-[2rem_1fr_auto] gap-3 items-center px-6 pt-3.5 pb-1.5">
        <span className="text-[1.15rem] text-center">{codeToFlag(order.country)}</span>
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="font-mono text-[0.75rem] font-semibold text-foreground">
            #{order.displayOrderId}
          </span>
          {order.orderSource && (
            <span className="text-[0.65rem] text-fg-dim bg-foreground/5 px-1.5 py-0.5 rounded">
              {order.orderSource}
            </span>
          )}
          {isNew && (
            <span className="text-[0.6rem] font-semibold text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-1.5 py-0.5 rounded animate-pulse">
              {t('orderFeed.newBadge')}
            </span>
          )}
        </div>
        <span className="font-mono text-[0.85rem] font-bold text-fg-bright">
          {fmt(order.total)}
        </span>
      </div>

      {/* Line items */}
      <div className="px-6 pb-3 pl-[4.25rem]">
        {order.lines.map((line, i) => (
          <div
            key={`${line.sku}-${i}`}
            className={`grid grid-cols-[24px_1fr_auto_auto] gap-2.5 items-center py-1${line.skuKey ? ' cursor-pointer hover:bg-foreground/[0.03] -mx-2 px-2 rounded-md transition-colors' : ''}`}
            onClick={() => handleLineClick(line)}
          >
            <img
              src={line.imageUrl || FALLBACK_IMG}
              alt=""
              className="w-6 h-6 rounded-[5px] object-cover bg-muted/30"
              loading="lazy"
              onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK_IMG }}
            />
            <span className="text-[0.78rem] text-fg-muted truncate">
              {line.name}
            </span>
            <span className="font-mono text-[0.7rem] text-fg-dim text-right">
              x{line.quantity}
            </span>
            <span className="font-mono text-[0.75rem] font-medium text-fg-muted text-right min-w-[60px]">
              {fmt(line.amount)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
